import { useState } from 'react';
import { Link } from 'react-router-dom';
import GeometryTestCard from './components/GeometryTestCard';
import { geometryTestNY } from './data/geometryTestNY';
import { geometryTestOH } from './data/geometryTestOH';
import { geometryTestTX } from './data/geometryTestTX';
import { geometryTestVA } from './data/geometryTestVA';
import { geometryTestPA } from './data/geometryTestPA';
import type { GeometryQuestion } from './data/geometryTestNY';

interface ActiveTest {
  title: string;
  stateName: string;
  stateColor: string;
  questions: GeometryQuestion[];
}

const tests = [
  {
    state: 'New York',
    abbr: 'NY',
    title: 'NY Regents Geometry',
    subtitle: 'New York State Regents Examination — Geometry',
    description: 'Aligned to the NY Regents Geometry curriculum covering transformations, congruence, similarity, trigonometry, circles, and coordinate geometry.',
    questions: geometryTestNY,
    color: 'from-teal-600 to-emerald-700',
    cardColor: 'from-teal-500 to-emerald-600',
    bgLight: 'bg-teal-50',
    borderColor: 'border-teal-200',
    textColor: 'text-teal-700',
    icon: 'ri-building-2-fill',
    flag: '🗽',
    topics: ['Transformations', 'Triangle Congruence', 'Similarity', 'Trigonometry', 'Circles', 'Coordinate Geometry', 'Volume'],
    difficulty: 'Regents Level',
    difficultyColor: 'bg-teal-100 text-teal-700',
  },
  {
    state: 'Ohio',
    abbr: 'OH',
    title: 'Ohio Geometry Assessment',
    subtitle: 'Ohio Department of Education — Geometry Standards',
    description: 'Covers all 15 Ohio Geometry standards including rigid motions, proofs, trigonometry, circles, volume, and coordinate geometry.',
    questions: geometryTestOH,
    color: 'from-red-600 to-rose-700',
    cardColor: 'from-red-500 to-rose-600',
    bgLight: 'bg-red-50',
    borderColor: 'border-red-200',
    textColor: 'text-red-700',
    icon: 'ri-government-fill',
    flag: '🌻',
    topics: ['Rigid Motions', 'Congruence Proofs', 'Trigonometry', 'Circle Theorems', 'Volume', 'Coordinate Geometry', 'Constructions'],
    difficulty: 'State Level',
    difficultyColor: 'bg-red-100 text-red-700',
  },
  {
    state: 'Texas',
    abbr: 'TX',
    title: 'Texas STAAR Geometry',
    subtitle: 'Texas TEKS — State of Texas Assessments of Academic Readiness',
    description: 'Aligned to Texas TEKS for Geometry, covering logical reasoning, coordinate geometry, transformations, trigonometry, and 3D figures.',
    questions: geometryTestTX,
    color: 'from-amber-600 to-orange-700',
    cardColor: 'from-amber-500 to-orange-600',
    bgLight: 'bg-amber-50',
    borderColor: 'border-amber-200',
    textColor: 'text-amber-700',
    icon: 'ri-star-fill',
    flag: '⭐',
    topics: ['Logical Reasoning', 'Parallel Lines', 'Trigonometry', 'Coordinate Geometry', 'Circles', 'Volume', 'Transformations'],
    difficulty: 'STAAR Level',
    difficultyColor: 'bg-amber-100 text-amber-700',
  },
  {
    state: 'Virginia',
    abbr: 'VA',
    title: 'Virginia SOL Geometry',
    subtitle: 'Virginia Standards of Learning — Geometry',
    description: 'Covers Virginia SOL Geometry standards including reasoning & proof, lines & angles, triangles, quadrilaterals, circles, and 3D figures.',
    questions: geometryTestVA,
    color: 'from-indigo-600 to-violet-700',
    cardColor: 'from-indigo-500 to-violet-600',
    bgLight: 'bg-indigo-50',
    borderColor: 'border-indigo-200',
    textColor: 'text-indigo-700',
    icon: 'ri-map-pin-2-fill',
    flag: '🏛️',
    topics: ['Reasoning & Proof', 'Triangle Properties', 'Quadrilaterals', 'Similarity', 'Circles', 'Trigonometry', 'Transformations'],
    difficulty: 'SOL Level',
    difficultyColor: 'bg-indigo-100 text-indigo-700',
  },
  {
    state: 'Pennsylvania',
    abbr: 'PA',
    title: 'Pennsylvania Keystone Geometry',
    subtitle: 'Pennsylvania Core Standards — Geometry',
    description: 'Aligned to PA Core Standards for Geometry, covering congruence, similarity, trigonometry, circles, coordinate geometry, and 3D measurement.',
    questions: geometryTestPA,
    color: 'from-emerald-700 to-teal-800',
    cardColor: 'from-emerald-600 to-teal-700',
    bgLight: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    textColor: 'text-emerald-700',
    icon: 'ri-shield-fill',
    flag: '🔔',
    topics: ['Angle Pairs', 'Triangle Congruence', 'Similarity', 'Circles', 'Coordinate Geometry', 'Volume', 'Proofs'],
    difficulty: 'Keystone Level',
    difficultyColor: 'bg-emerald-100 text-emerald-700',
  },
];

const topicCoverage = [
  { icon: 'ri-shapes-line', label: 'Points, Lines & Planes', desc: 'Foundations of Euclidean geometry' },
  { icon: 'ri-arrow-right-up-line', label: 'Transformations', desc: 'Translations, reflections, rotations, dilations' },
  { icon: 'ri-triangle-line', label: 'Triangle Congruence & Similarity', desc: 'SSS, SAS, ASA, AAS, AA, SAS~, SSS~' },
  { icon: 'ri-compass-3-line', label: 'Circles', desc: 'Arcs, chords, tangents, inscribed angles' },
  { icon: 'ri-ruler-line', label: 'Right Triangles & Trig', desc: 'SOH-CAH-TOA, special triangles, inverse trig' },
  { icon: 'ri-cube-line', label: 'Area, Surface Area & Volume', desc: 'All 2D and 3D figures' },
  { icon: 'ri-map-2-line', label: 'Coordinate Geometry', desc: 'Distance, midpoint, slope, equations' },
  { icon: 'ri-file-text-line', label: 'Proofs & Reasoning', desc: 'Two-column proofs, deductive reasoning' },
];

export default function GeometryTestsPage() {
  const [activeTest, setActiveTest] = useState<ActiveTest | null>(null);

  if (activeTest) {
    return (
      <GeometryTestCard
        testTitle={activeTest.title}
        stateName={activeTest.stateName}
        stateColor={activeTest.stateColor}
        questions={activeTest.questions}
        onClose={() => setActiveTest(null)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow">
                <i className="ri-graduation-cap-fill text-white text-xl"></i>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Mr. Augustine's Math Academy</h1>
                <p className="text-xs text-emerald-600 font-medium">Geometry — State Practice Tests</p>
              </div>
            </Link>
            <div className="flex items-center gap-3">
              <Link
                to="/grades/grade-10"
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm whitespace-nowrap"
              >
                <i className="ri-book-open-line mr-2"></i>Geometry Lessons
              </Link>
              <Link
                to="/grades"
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium text-sm whitespace-nowrap"
              >
                <i className="ri-arrow-left-line mr-2"></i>All Grades
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Banner */}
      <div className="bg-gradient-to-r from-emerald-800 via-teal-700 to-emerald-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="flex flex-col md:flex-row items-center gap-10">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full text-sm font-semibold mb-5">
                <i className="ri-award-fill text-amber-300"></i>
                Official State Geometry Assessments — 5 States
              </div>
              <h2 className="text-4xl md:text-5xl font-black mb-4 leading-tight">
                Geometry State<br />
                <span className="text-amber-300">Practice Tests</span>
              </h2>
              <p className="text-emerald-100 text-lg leading-relaxed mb-6 max-w-xl">
                Authentic state-exam-style questions from New York, Ohio, Texas, Virginia, and Pennsylvania.
                Every question includes a complete step-by-step solution — just like the real exams.
              </p>
              <div className="flex flex-wrap gap-3">
                {[
                  { icon: 'ri-map-pin-line', label: '5 States Covered' },
                  { icon: 'ri-question-line', label: '150 Total Questions' },
                  { icon: 'ri-lightbulb-line', label: 'Full Solutions Included' },
                  { icon: 'ri-bar-chart-line', label: 'Instant Scoring' },
                ].map((badge, i) => (
                  <div key={i} className="flex items-center gap-2 bg-white/15 px-4 py-2 rounded-full text-sm font-medium">
                    <i className={`${badge.icon} text-amber-300`}></i>
                    {badge.label}
                  </div>
                ))}
              </div>
            </div>
            <div className="flex-shrink-0 grid grid-cols-3 gap-3">
              {tests.map(t => (
                <div key={t.abbr} className="w-16 h-16 bg-white/15 rounded-2xl flex flex-col items-center justify-center border border-white/20 shadow-lg">
                  <span className="text-2xl">{t.flag}</span>
                  <span className="text-xs font-bold text-white/90 mt-1">{t.abbr}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Teacher Message */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-6 flex items-start gap-5">
          <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
            <i className="ri-user-smile-fill text-white text-2xl"></i>
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-1">A Message from Mr. Augustine</h3>
            <p className="text-gray-600 leading-relaxed text-sm">
              "Geometry is tested differently in every state — but the core concepts are universal. Whether you are preparing for the
              New York Regents, the Texas STAAR, the Virginia SOL, the Ohio state assessment, or the Pennsylvania Keystone exam,
              these practice tests will prepare you for all of them. Master the concepts, understand the reasoning, and you will
              succeed on any geometry exam in the country. I have seen it happen hundreds of times — and you are next!"
            </p>
            <p className="text-emerald-600 font-bold text-sm mt-2">— Mr. Augustine Anyimadu-Ahenkae, Former NYC Public High School Math Teacher · 25+ Years Experience</p>
          </div>
        </div>
      </div>

      {/* Topics Covered */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl p-6 text-white">
          <h3 className="text-lg font-bold mb-5 flex items-center gap-2">
            <i className="ri-list-check-2 text-amber-300"></i>
            Geometry Topics Covered Across All 5 State Tests
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {topicCoverage.map((topic, i) => (
              <div key={i} className="bg-white/15 rounded-xl p-4">
                <i className={`${topic.icon} text-amber-300 text-xl mb-2 block`}></i>
                <div className="font-bold text-sm">{topic.label}</div>
                <div className="text-emerald-200 text-xs mt-1">{topic.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* State Test Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Choose a State Practice Test</h2>
        <p className="text-gray-500 text-sm mb-8">Each test has 30 questions with complete step-by-step solutions. Take it like the real exam — then review every answer.</p>

        {/* NY Regents Featured Banner */}
        <div className="mb-8 bg-gradient-to-r from-emerald-800 via-teal-700 to-emerald-800 rounded-2xl shadow-xl overflow-hidden">
          <div className="p-8 flex flex-col md:flex-row items-center gap-8">
            <div className="flex-shrink-0 flex items-center gap-4">
              <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center text-5xl">🗽</div>
              <div className="text-white">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-400/30 rounded-full text-xs font-bold text-amber-300 mb-2">
                  <i className="ri-star-fill"></i> FEATURED — FULL REGENTS EXAM
                </div>
                <h3 className="text-2xl font-black">NY Geometry Regents</h3>
                <p className="text-emerald-200 text-sm">Open-Ended Parts II, III &amp; IV Included</p>
              </div>
            </div>
            <div className="flex-1 text-white">
              <p className="text-emerald-100 text-sm leading-relaxed mb-4">
                The only practice exam with all four Regents parts — including open-ended short answer, extended response, and the 6-point modeling problem. Every question has a full rubric and step-by-step solution, just like the real NY Regents.
              </p>
              <div className="flex flex-wrap gap-3 mb-5">
                {[
                  { icon: 'ri-file-list-3-line', label: 'Parts I, II, III & IV' },
                  { icon: 'ri-question-line', label: '35 Questions' },
                  { icon: 'ri-award-line', label: '86 Total Points' },
                  { icon: 'ri-list-check-2', label: 'Full Rubrics' },
                ].map((b, i) => (
                  <div key={i} className="flex items-center gap-2 bg-white/15 px-3 py-1.5 rounded-full text-xs font-medium">
                    <i className={`${b.icon} text-amber-300`}></i>
                    {b.label}
                  </div>
                ))}
              </div>
              <Link
                to="/grades/geometry-regents"
                className="inline-flex items-center gap-2 px-7 py-3 bg-amber-400 text-gray-900 rounded-xl font-black text-sm hover:bg-amber-300 transition-colors shadow-lg whitespace-nowrap"
              >
                <i className="ri-play-circle-fill text-lg"></i>
                Take the Full NY Regents Exam
              </Link>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tests.map(test => (
            <div
              key={test.abbr}
              className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden group"
            >
              {/* Card Header */}
              <div className={`bg-gradient-to-r ${test.cardColor} p-6 text-white`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center text-2xl">
                      {test.flag}
                    </div>
                    <div>
                      <div className="text-white/80 text-xs font-medium uppercase tracking-wide">{test.state}</div>
                      <div className="text-xl font-black">{test.abbr} Geometry</div>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${test.difficultyColor}`}>
                    {test.difficulty}
                  </span>
                </div>
                <p className="text-white/80 text-sm leading-relaxed">{test.subtitle}</p>
              </div>

              {/* Card Body */}
              <div className="p-6">
                <p className="text-gray-600 text-sm mb-4 leading-relaxed">{test.description}</p>

                <div className="flex items-center gap-4 mb-4 text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <i className="ri-question-line text-emerald-500"></i>
                    <span>{test.questions.length} questions</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <i className="ri-time-line text-emerald-500"></i>
                    <span>~45–60 min</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <i className="ri-star-line text-amber-500"></i>
                    <span>{test.questions.reduce((a, q) => a + q.points, 0)} pts</span>
                  </div>
                </div>

                <div className="mb-5">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Topics Covered</p>
                  <div className="flex flex-wrap gap-1.5">
                    {test.topics.map((topic, i) => (
                      <span key={i} className={`px-2 py-1 ${test.bgLight} ${test.textColor} rounded-full text-xs font-medium whitespace-nowrap`}>
                        {topic}
                      </span>
                    ))}
                  </div>
                </div>

                <button
                  onClick={() => setActiveTest({
                    title: test.title,
                    stateName: test.state,
                    stateColor: test.color,
                    questions: test.questions,
                  })}
                  className={`w-full py-3 bg-gradient-to-r ${test.cardColor} text-white rounded-xl font-bold text-sm hover:opacity-90 transition-opacity shadow-md group-hover:shadow-lg whitespace-nowrap cursor-pointer`}
                >
                  <i className="ri-play-circle-fill mr-2"></i>
                  Start {test.abbr} Geometry Test
                </button>
              </div>
            </div>
          ))}

          {/* Tips Card */}
          <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-2xl border-2 border-amber-200 p-6 flex flex-col justify-between">
            <div>
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-4">
                <i className="ri-lightbulb-fill text-amber-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-black text-gray-900 mb-3">Geometry Exam Tips</h3>
              <ul className="space-y-3">
                {[
                  'Draw and label a diagram for every problem — even if one is given.',
                  'Write out the theorem or formula you are using before applying it.',
                  'For proofs, start with what is given and work toward what you must prove.',
                  'In trigonometry, always identify which angle is the reference angle first.',
                  'For circle problems, mark all radii, diameters, and given arc measures.',
                  'Check units — area is square units, volume is cubic units.',
                  'Use the Pythagorean Theorem to verify right triangles before using trig.',
                ].map((tip, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="w-5 h-5 bg-amber-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-6 p-4 bg-amber-100 rounded-xl">
              <p className="text-xs text-amber-800 font-semibold text-center">
                <i className="ri-award-fill mr-1"></i>
                Passing score varies by state — aim for 80%+ to be exam-ready!
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Standards Comparison */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-3">
            <i className="ri-map-2-line text-emerald-600 text-2xl"></i>
            State Standards Comparison
          </h3>
          <p className="text-gray-500 text-sm mb-6">All five state tests are built on the same CCSS Geometry foundation — with state-specific emphases.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 rounded-xl">
                  <th className="text-left px-4 py-3 font-bold text-gray-700 rounded-l-xl">State</th>
                  <th className="text-left px-4 py-3 font-bold text-gray-700">Exam Name</th>
                  <th className="text-left px-4 py-3 font-bold text-gray-700">Standards Framework</th>
                  <th className="text-left px-4 py-3 font-bold text-gray-700">Key Emphasis</th>
                  <th className="text-left px-4 py-3 font-bold text-gray-700 rounded-r-xl">Questions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[
                  { state: '🗽 New York', exam: 'Regents Geometry', framework: 'NY CCSS + Regents', emphasis: 'Proofs, Transformations, Coordinate Geometry', q: 30 },
                  { state: '🌻 Ohio', exam: 'Ohio State Assessment', framework: 'Ohio Learning Standards', emphasis: 'Constructions, Cavalieri\'s Principle, Modeling', q: 30 },
                  { state: '⭐ Texas', exam: 'STAAR Geometry', framework: 'Texas TEKS', emphasis: 'Logical Reasoning, Density, Conditional Statements', q: 30 },
                  { state: '🏛️ Virginia', exam: 'SOL Geometry', framework: 'Virginia SOL', emphasis: 'Inductive/Deductive Reasoning, CPCTC, Locus', q: 30 },
                  { state: '🔔 Pennsylvania', exam: 'Keystone Geometry', framework: 'PA Core Standards', emphasis: 'Geometric Probability, Frustum, Circumcenter', q: 30 },
                ].map((row, i) => (
                  <tr key={i} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-semibold text-gray-900">{row.state}</td>
                    <td className="px-4 py-3 text-gray-700">{row.exam}</td>
                    <td className="px-4 py-3 text-gray-600">{row.framework}</td>
                    <td className="px-4 py-3 text-gray-600">{row.emphasis}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-bold">{row.q} Qs</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="bg-gradient-to-r from-emerald-700 to-teal-700 rounded-2xl shadow-2xl p-8 md:p-12 text-center text-white">
          <div className="max-w-3xl mx-auto">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-vip-crown-fill text-4xl"></i>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Master Geometry?</h2>
            <p className="text-emerald-100 text-lg mb-8 leading-relaxed">
              Combine these state practice tests with Mr. Augustine's 10 full interactive Geometry lessons,
              300+ practice questions, and printable worksheets for complete exam preparation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/grades/grade-10"
                className="px-8 py-4 bg-white text-emerald-700 rounded-xl font-bold text-lg hover:bg-emerald-50 transition-colors shadow-lg whitespace-nowrap"
              >
                <i className="ri-book-open-fill mr-2"></i>Go to Geometry Lessons
              </Link>
              <Link
                to="/#membership"
                className="px-8 py-4 bg-emerald-800 text-white rounded-xl font-bold text-lg hover:bg-emerald-900 transition-colors whitespace-nowrap"
              >
                <i className="ri-vip-crown-fill mr-2"></i>Become a Member
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="w-9 h-9 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
                <i className="ri-graduation-cap-fill text-white"></i>
              </div>
              <h3 className="text-lg font-bold text-white">Mr. Augustine's Math Academy</h3>
            </div>
            <p className="text-gray-400 text-sm max-w-xl mx-auto">
              Empowering every student to master mathematics through proven teaching methods, personalized support, and rigorous practice.
            </p>
            <p className="text-xs text-gray-500 mt-4">© 2024 Mr. Augustine's Math Academy. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
